import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import React, { useRef, useState } from "react";
import { Card, Col, Container, Row } from "react-bootstrap";
import "react-multi-carousel/lib/styles.css";

import {
  faGear,
  faGears,
  faHandPointRight,
  faLayerGroup,
  faLightbulb,
  faSquareCheck,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import HomeCarousel from "./components/carousel";
import Lightbox from "./components/lightbox";
import data from "./data/data.json";

import customerModelsQPSImage from "./assets/customer_models_qps.png";
import dynamicCrashesLoadCasesImage from "./assets/dynamic_crash_load_cases.png";
import modalSimulationsImage from "./assets/modal_simulations.png";
import officeImg from "./assets/office.jpg";
import officeImg2 from "./assets/office2.jpg";
import quasiStaticLoadImage from "./assets/quasi_static_load.png";
import shellMeshImage from "./assets/shell_mesh.png";
import solidHexMeshImage from "./assets/solid_hex_mesh.png";
import solidHybridMeshImage from "./assets/solid_hybrid_mesh.png";
import solidTetraMeshImage from "./assets/solid_tetra_mesh.png";
import staticAbuseLoadImage from "./assets/static_abuse_load.png";
import {
  CpuIcon,
  CuboidIcon,
  DraftingCompassIcon,
  LayersIcon,
} from "./assets/svg";
import "./styles/home.css";

const aboutData = data.about;
const infrastructureData = data.infrastructure;
const capabilitiesData = data.capabilities;
const advantagesData = data.advantages;

gsap.registerPlugin(useGSAP, ScrollTrigger);

const Home = () => {
  const aboutRef = useRef();
  const infrastructureRef1 = useRef();
  const infrastructureRef2 = useRef();
  const vitalsRef = useRef();
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const images = [
    { src: solidHexMeshImage, alt: "Solid Hex Mesh", label: "Solid Hex Mesh" },
    {
      src: solidTetraMeshImage,
      alt: "Solid Tetra Mesh",
      label: "Solid Tetra Mesh",
    },
    {
      src: solidHybridMeshImage,
      alt: "Solid Hybrid Mesh",
      label: "Solid Hybrid Mesh",
    },
    { src: shellMeshImage, alt: "Shell Mesh", label: "Shell Mesh" },
    {
      src: customerModelsQPSImage,
      alt: "Customer Models, QPS",
      label: "Customer Models, QPS",
    },
    {
      src: dynamicCrashesLoadCasesImage,
      alt: "Dynamic/Crash Load Cases",
      label: "Dynamic/Crash Load Cases",
    },
    {
      src: quasiStaticLoadImage,
      alt: "Quasi-Static Load Cases",
      label: "Quasi-Static Load Cases",
    },
    {
      src: staticAbuseLoadImage,
      alt: "Static Abuse Load Cases",
      label: "Static Abuse Load Cases",
    },
    {
      src: modalSimulationsImage,
      alt: "Modal Simulations",
      label: "Modal Simulations",
    },
  ];

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => setIsLightboxOpen(false);

  useGSAP(() => {
    gsap.fromTo(
      aboutRef.current,
      { x: -100, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        scrollTrigger: {
          trigger: aboutRef.current,
          start: "top 90%",
          end: "top 45%",
          toggleActions: "play none none reverse", // Play on enter and reverse on leave
          scrub: true,
        },
      }
    );

    gsap.fromTo(
      infrastructureRef1.current,
      { x: 100, opacity: 0, display: "none" },
      {
        x: 0,
        opacity: 1,
        display: "block",
        scrollTrigger: {
          trigger: infrastructureRef1.current,
          start: "top 90%",
          end: "top 45%",
          toggleActions: "play none none reverse",
          scrub: true,
        },
      }
    );

    gsap.fromTo(
      infrastructureRef2.current,
      { x: -100, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        scrollTrigger: {
          trigger: infrastructureRef2.current,
          start: "top 90%",
          end: "top 45%",
          toggleActions: "play none none reverse",
          scrub: true,
        },
      }
    );

    gsap.fromTo(
      vitalsRef.current,
      { y: 100, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        scrollTrigger: {
          trigger: vitalsRef.current,
          start: "top 90%",
          end: "top 50%",
          toggleActions: "play none none reverse",
          scrub: true,
        },
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  });

  return (
    <div id="home">
      <HomeCarousel />
      <Container>
        {/* ABOUT US */}
        <section className="aboutus-section" id="about">
          <h1>{aboutData.title}</h1>
          <div className="data-section">
            <div className="text-container" ref={aboutRef}>
              <div className="about-data">
                {aboutData.description.map((item, index) => (
                  <p key={index}>{item}</p>
                ))}
              </div>
            </div>
            <div className="about-img-container">
              <img src={officeImg} alt="office_img" />
            </div>
          </div>
        </section>

        {/* INFRASTRUCTURE */}
        <section className="infrastructure-section" id="infrastructure">
          <h1>{infrastructureData.title}</h1>

          <div className="infrastructure-data-section">
            <div className="infrastructure-img-container">
              <img src={officeImg2} alt="office_img" />
            </div>
            <div
              className="infrastructure-text-container"
              ref={infrastructureRef1}
            >
              <div className="infrastructure-data">
                {infrastructureData.description.map((item, index) => (
                  <p key={index}>{item}</p>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* BUSINESS VERTICALS */}
        <section className="verticals-section" id="vitals">
          <h1>BUSINESS VERTICALS</h1>

          <div className="verticals-container" ref={vitalsRef}>
            <div className="verticals">
              <FontAwesomeIcon icon={faLightbulb} color="white" size="5x" />
              training
            </div>
            <div className="verticals">
              <FontAwesomeIcon icon={faGears} color="white" size="5x" />
              staffing services
            </div>
            <div className="verticals">
              <FontAwesomeIcon icon={faGear} color="white" size="5x" />
              engineering services
            </div>
            <div className="verticals">
              <FontAwesomeIcon icon={faLayerGroup} color="white" size="5x" />
              Validation Support and DESIGN
            </div>
          </div>
        </section>

        {/* CAPABILITIES */}
        <section className="capabilities-section">
          <h1>{capabilitiesData.title}</h1>
          <div className="capabilities-data">
            {capabilitiesData.description.map((item, index) => (
              <p key={index}>
                <FontAwesomeIcon
                  icon={faHandPointRight}
                  color="#001538"
                  style={{ marginRight: "8px" }}
                />
                {item}
              </p>
            ))}
          </div>
        </section>

        <section className="capabilities-images-section">
          <h3>Meshing Examples</h3>
          <div className="meshing-examples">
            {images.slice(0, 4).map((image, index) => (
              <div
                className="examples"
                key={index}
                onClick={() => openLightbox(index)}
              >
                <img src={image.src} alt={image.alt} />
                <p>{image.label}</p>
              </div>
            ))}
          </div>

          <h3>Model Integration</h3>
          <div className="meshing-examples">
            <div className="examples" onClick={() => openLightbox(4)}>
              <img src={images[4].src} alt={images[4].alt} />
              <p>{images[4].label}</p>
            </div>
          </div>

          <h3>Type of Load-case Setup</h3>
          <div className="meshing-examples">
            {images.slice(5).map((image, index) => (
              <div
                className="examples"
                key={index + 5}
                onClick={() => openLightbox(index + 5)}
              >
                <img src={image.src} alt={image.alt} />
                <p>{image.label}</p>
              </div>
            ))}
          </div>

          {/* Lightbox Component */}
          {isLightboxOpen && (
            <Lightbox
              images={images}
              startIndex={currentImageIndex}
              onClose={closeLightbox}
            />
          )}
        </section>

        {/* SERVICES */}
        <section className="services-section" id="services">
          <div className="services-text">
            <h1>SERVICES</h1>
            <p>
              We offer a comprehensive suite of engineering and design services
              to help you bring your ideas to life.
            </p>
          </div>
          <Row className="gx-4 gy-4">
            <Col sm={12} md={6} lg={4}>
              <Card className="service-card">
                <Card.Body>
                  <Card.Title as="h4">
                    <p style={{ textAlign: "center" }}>
                      <CpuIcon className="serives-icons" />
                      CAE/FEA
                    </p>
                  </Card.Title>
                  <Card.Text className="text-muted-foreground">
                    Our team of experts specializes in advanced computational
                    analysis and finite element analysis (FEA) to help you
                    optimize your designs and ensure structural integrity.
                  </Card.Text>
                </Card.Body>
              </Card>
              <Card className="mt-4 service-card">
                <Card.Body>
                  <Card.Title as="h4">
                    <p style={{ textAlign: "center" }}>
                      <FontAwesomeIcon
                        icon={faSquareCheck}
                        style={{ marginRight: "8px" }}
                      />
                      HyperMesh Training
                    </p>
                  </Card.Title>
                  <Card.Text className="text-muted-foreground">
                    We offer comprehensive training on HyperMesh, a leading
                    pre-processing tool for FEA, to help you streamline your
                    simulation workflows.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>

            <Col sm={12} md={6} lg={4}>
              <Card className="service-card">
                <Card.Body>
                  <Card.Title as="h4">
                    <p style={{ textAlign: "center" }}>
                      <LayersIcon className="serives-icons" />
                      LS-Dyna
                    </p>
                  </Card.Title>
                  <Card.Text className="text-muted-foreground">
                    Our experts are well-versed in LS-Dyna, a powerful
                    finite-element analysis software for simulating complex
                    engineering problems, including nonlinear dynamics and
                    impact analysis.
                  </Card.Text>
                </Card.Body>
              </Card>
              <Card className="mt-4 service-card">
                <Card.Body>
                  <Card.Title as="h4">
                    <p style={{ textAlign: "center" }}>
                      <CuboidIcon className="serives-icons" />
                      Optistruct/Nastran
                    </p>
                  </Card.Title>
                  <Card.Text className="text-muted-foreground">
                    We have extensive experience in Optistruct and Nastran, two
                    leading structural analysis and optimization tools, to help
                    you achieve optimal designs.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>

            <Col sm={12} md={12} lg={4}>
              <Row className="gx-4 gy-4">
                <Col sm={12} md={6} lg={12}>
                  <Card className="service-card">
                    <Card.Body>
                      <Card.Title as="h4">
                        <p style={{ textAlign: "center" }}>
                          <DraftingCompassIcon className="serives-icons" />
                          CAD Services
                        </p>
                      </Card.Title>
                      <Card.Text className="text-muted-foreground">
                        Our team of skilled CAD designers can help you create
                        high-quality, detailed 3D models and drawings for your
                        projects, using the latest CAD software and
                        technologies.
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col sm={12} md={6} lg={12}>
                  <Card className="service-card">
                    <Card.Body>
                      <Card.Title as="h4">
                        <p style={{ textAlign: "center" }}>
                          <FontAwesomeIcon
                            icon={faUsers}
                            style={{ marginRight: "8px" }}
                          />
                          Technical staffing solutions
                        </p>
                      </Card.Title>
                      <Card.Text className="text-muted-foreground">
                        We offer Technical staffing solutions with Trained
                        professionals deputed on-site. <br />
                        We also handle In-House Projects.
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </section>

        {/* ADVANTAGES/UVP */}
        <section className="advantages-section">
          <h1>{advantagesData.title}</h1>
          <div className="advantages-container">
            <p className="advantages-content">
              In the fast-paced world of today, it can be difficult for both
              individuals and organizations to stay up to date with changing
              knowledge and abilities. CHECS stands out as a symbol of
              adaptability and commitment in the face of these changing demands.
              Personalized training, problem-solving, and project solutions that
              can be delivered at any time and from any location. The
              customer-centric approach implies that the solutions are flexible
              and client-centered.
            </p>
            {/* <div className="advantages-column">
              {advantagesData.description
                .slice(0, Math.ceil(advantagesData.description.length / 2))
                .map((item, index) => (
                  <p key={index} className="advantages-text">
                    <FontAwesomeIcon
                      icon={faCheck}
                      color="#0070c0"
                      style={{ marginRight: "8px" }}
                    />
                    {item}
                  </p>
                ))}
            </div>
            <div className="advantages-column">
              {advantagesData.description
                .slice(Math.ceil(advantagesData.description.length / 2))
                .map((item, index) => (
                  <p key={index} className="advantages-text">
                    <FontAwesomeIcon
                      icon={faCheck}
                      color="#0070c0"
                      style={{ marginRight: "8px" }}
                    />
                    {item}
                  </p>
                ))}
            </div> */}
          </div>
        </section>
      </Container>
    </div>
  );
};

export default Home;
